import { getJson, getList, sendJson } from '@/lib/api';
import type { OrganizationSummary } from '@/lib/types';
import { todayIso } from '@/lib/dev-tracker';

/**
 * Overview promo banners (Roy's portal list, item 10; 2026-09-29). Mirrors the
 * API's `promo-banners.domain.ts`: kinds, audiences, statuses, the link rule
 * and the per-portal cap. The API is the authority; the copies here let the
 * form say what is wrong before anyone presses Save.
 */

export const BANNER_KIND = {
  seasonal: 'SEASONAL',
  largeEvent: 'LARGE_EVENT',
  expiringCredits: 'EXPIRING_CREDITS',
  companyMarker: 'COMPANY_MARKER',
  nationalObservance: 'NATIONAL_OBSERVANCE',
  custom: 'CUSTOM',
} as const;
export type BannerKind = (typeof BANNER_KIND)[keyof typeof BANNER_KIND];

/** The kinds, with the words people see. */
export const BANNER_KIND_OPTIONS: readonly { readonly key: BannerKind; readonly label: string }[] = [
  { key: BANNER_KIND.seasonal, label: 'Seasonal' },
  { key: BANNER_KIND.largeEvent, label: 'Large event' },
  { key: BANNER_KIND.expiringCredits, label: 'Expiring credits' },
  { key: BANNER_KIND.companyMarker, label: 'Company milestone' },
  { key: BANNER_KIND.nationalObservance, label: 'National observance' },
  { key: BANNER_KIND.custom, label: 'Other' },
];

export const BANNER_AUDIENCE = {
  allPortals: 'ALL_PORTALS',
  organizations: 'ORGANIZATIONS',
} as const;
export type BannerAudience = (typeof BANNER_AUDIENCE)[keyof typeof BANNER_AUDIENCE];

export const BANNER_STATUS = {
  scheduled: 'SCHEDULED',
  live: 'LIVE',
  ended: 'ENDED',
} as const;
export type BannerStatus = (typeof BANNER_STATUS)[keyof typeof BANNER_STATUS];

export const BANNER_STATUS_OPTIONS: readonly { readonly key: BannerStatus; readonly label: string }[] = [
  { key: BANNER_STATUS.live, label: 'Live' },
  { key: BANNER_STATUS.scheduled, label: 'Scheduled' },
  { key: BANNER_STATUS.ended, label: 'Ended' },
];

/** The most banners one portal shows at once (the API caps the carousel). */
export const MAX_LIVE_BANNERS_PER_PORTAL = 3;

/** Field lengths, matching the API's `BANNER_LIMITS`. */
export const BANNER_LIMITS = {
  eyebrow: 40,
  headline: 90,
  body: 280,
  ctaLabel: 40,
  ctaHref: 500,
} as const;

export const kindLabel = (kind: BannerKind): string =>
  BANNER_KIND_OPTIONS.find((option) => option.key === kind)?.label ?? kind;
export const statusLabel = (status: BannerStatus): string =>
  BANNER_STATUS_OPTIONS.find((option) => option.key === status)?.label ?? status;

/** An organization a banner reaches. */
export interface BannerOrganization {
  readonly id: string;
  readonly name: string;
}

/** A banner, as `/promo-banners` sends it. */
export interface PromoBanner {
  readonly id: string;
  readonly kind: BannerKind;
  readonly eyebrow: string;
  readonly headline: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly ctaHref: string;
  readonly audience: BannerAudience;
  readonly organizations: readonly BannerOrganization[];
  readonly startsOn: string | null;
  readonly endsOn: string | null;
  readonly status: BannerStatus;
  /** Other banners that could share a portal with it during its window. */
  readonly overlapping: number;
  readonly createdBy: string | null;
  readonly createdAt: string;
}

/** What the form sends. */
export interface PromoBannerInput {
  readonly kind: BannerKind;
  readonly eyebrow: string;
  readonly headline: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly ctaHref: string;
  readonly audience: BannerAudience;
  readonly organizationIds: readonly string[];
  readonly startsOn: string;
  readonly endsOn: string;
}

const BASE = '/promo-banners';

export const listPromoBanners = (signal?: AbortSignal): Promise<PromoBanner[]> =>
  getJson<PromoBanner[]>(BASE, undefined, signal);

export const createPromoBanner = (input: PromoBannerInput): Promise<PromoBanner> =>
  sendJson<PromoBanner>('post', BASE, input);

export const updatePromoBanner = (id: string, input: PromoBannerInput): Promise<PromoBanner> =>
  sendJson<PromoBanner>('put', `${BASE}/${id}`, input);

export const removePromoBanner = (id: string): Promise<void> => sendJson<void>('delete', `${BASE}/${id}`);

/** Client organizations for the audience picker. The picker lists up to the API's page limit. */
const CLIENT_PICKER_LIMIT = 100;

export async function listClientOrganizations(signal?: AbortSignal): Promise<BannerOrganization[]> {
  const { items } = await getList<OrganizationSummary>(
    '/organizations',
    { kind: 'CLIENT', status: 'ACTIVE', limit: CLIENT_PICKER_LIMIT },
    signal,
  );
  return items.map((organization) => ({ id: organization.id, name: organization.name }));
}

/**
 * Whether a button link may be saved: a portal page (`/programs`) or an
 * https address. Mirrors the API's `isSafeCtaHref`.
 *
 * @param href - What was typed.
 * @returns True when the API will take it.
 */
export function isSafeCtaHref(href: string): boolean {
  const value = href.trim();
  if (value === '' || value !== href || /[\s\\]/.test(value)) return false;
  if (value.startsWith('/')) return !value.startsWith('//');
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname !== '' && url.username === '' && url.password === '';
  } catch {
    return false;
  }
}

/** Why a link was refused, in the form's words. */
export const CTA_LINK_HINT = 'A portal page such as /programs, or a full https:// address.';

/** The form's working copy of a banner. */
export interface BannerDraft {
  readonly kind: BannerKind;
  readonly eyebrow: string;
  readonly headline: string;
  readonly body: string;
  readonly ctaLabel: string;
  readonly ctaHref: string;
  readonly audience: BannerAudience;
  readonly organizationIds: readonly string[];
  readonly startsOn: string;
  readonly endsOn: string;
}

export function emptyDraft(): BannerDraft {
  return {
    kind: BANNER_KIND.seasonal,
    eyebrow: '',
    headline: '',
    body: '',
    ctaLabel: '',
    ctaHref: '',
    audience: BANNER_AUDIENCE.allPortals,
    organizationIds: [],
    startsOn: todayIso(),
    endsOn: '',
  };
}

export function draftOf(banner: PromoBanner): BannerDraft {
  return {
    kind: banner.kind,
    eyebrow: banner.eyebrow,
    headline: banner.headline,
    body: banner.body,
    ctaLabel: banner.ctaLabel,
    ctaHref: banner.ctaHref,
    audience: banner.audience,
    organizationIds: banner.organizations.map((organization) => organization.id),
    startsOn: banner.startsOn ?? '',
    endsOn: banner.endsOn ?? '',
  };
}

export function inputOf(draft: BannerDraft): PromoBannerInput {
  return {
    ...draft,
    eyebrow: draft.eyebrow.trim(),
    headline: draft.headline.trim(),
    body: draft.body.trim(),
    ctaLabel: draft.ctaLabel.trim(),
    ctaHref: draft.ctaHref.trim(),
    organizationIds: draft.audience === BANNER_AUDIENCE.organizations ? draft.organizationIds : [],
  };
}

/** What stops a draft saving, field by field. Empty when it can be saved. */
export function draftProblems(draft: BannerDraft): Partial<Record<keyof BannerDraft, string>> {
  const href = draft.ctaHref.trim();
  const label = draft.ctaLabel.trim();
  return {
    ...(draft.headline.trim() === '' ? { headline: 'Add a headline.' } : {}),
    ...(href !== '' && !isSafeCtaHref(href) ? { ctaHref: `That link won’t work. ${CTA_LINK_HINT}` } : {}),
    ...(href !== '' && label === '' ? { ctaLabel: 'Add the words on the button.' } : {}),
    ...(label !== '' && href === '' ? { ctaHref: `Add where the button goes. ${CTA_LINK_HINT}` } : {}),
    ...(draft.audience === BANNER_AUDIENCE.organizations && draft.organizationIds.length === 0
      ? { organizationIds: 'Choose at least one organization.' }
      : {}),
    ...(draft.startsOn === '' ? { startsOn: 'Choose a start date.' } : {}),
    ...(draft.endsOn === '' ? { endsOn: 'Choose an end date.' } : {}),
    ...(draft.startsOn !== '' && draft.endsOn !== '' && draft.endsOn < draft.startsOn
      ? { endsOn: 'The end date is before the start date.' }
      : {}),
  };
}
